// composables/useSeoInterData.ts
// Composable pour fetcher et gérer les données pages seo inter

import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { computed, ref, toValue, watch } from 'vue'
import { getAxiosResponseStatus } from '~/utils/axios'
import { parseBlock } from '~/utils/helpers'
import { EMPTY_SEO_BLOCK } from '~/constants/empty'
import type { SeoSiblingsBlock } from '~/types/school'
import {
  isEmptySeoInterApiPayload,
  logInvalidSeoPath,
  parseSeoRouteContext,
  resolveSeoParentRedirectPath,
  shouldRedirectToSeoParent,
  shouldSeoInterManageParentRedirect,
} from '~/utils/seoDeadPayloads'

export interface SeoInterData {
  h1: string
  introduction: string
  title: string
  description: string
  youTubeUrl: string
  parentBlock: string
  sistersBlock: string
  childrenBlock: string
  cousinsBlocks: string
  contentText: string
  partners: string
  programs: string
  articles: string
}

export interface SeoLink {
  Url: string
  Text: string
}

export interface PartnerObj {
  id: number
  Name: string
  Type: string
  Locations: string[]
  ProgramsCount: number
  BackgroundImage: string
  LogoImage: string
  Link: string
}

export interface BlogArticleObj {
  id: number | string
  Title: string
  Description: string
  Category: string
  ReadingTime: number
  Image: string
  Link: string
}

export interface ProgramObj {
  id: number
  Name: string
  Type: string
  Locations: string[]
  FormationsCount: number
  BackgroundImage: string
  LogoImage: string
  Link: string
}

export interface SeoInterDataResponse {
  seodatas: SeoInterData
  parsedParentBlock: SeoSiblingsBlock
  parsedSistersBlock: SeoSiblingsBlock
  parsedChildrenBlock: SeoSiblingsBlock
  parsedCousinsBlocks: SeoSiblingsBlock[]
  partners: PartnerObj[]
  articles: BlogArticleObj[]
  programs: ProgramObj[]
}

/** Chaîne (figée à l'appel du composable) ou getter réévalué à chaque cycle du watch : pour suivre la route, préférer `() => …`. */
export type SeoInterStringParam = string | (() => string | undefined)

export interface UseSeoInterDataOptions {
  language: MaybeRefOrGetter<string>
  pageType: string
  param1?: SeoInterStringParam
  param2?: SeoInterStringParam
}

/**
 * @param {SeoInterStringParam | undefined} v - Paramètre route ou getter.
 * @returns {string | undefined} Valeur résolue.
 */
function resolveSeoInterParam(v: SeoInterStringParam | undefined): string | undefined {
  if (v === undefined) return undefined
  return typeof v === 'function' ? v() : v
}

/**
 * @param {string} youtubeUrl - URL YouTube source.
 * @returns {string} URL d'embed ou chaîne vide.
 */
function getYouTubeEmbedUrl(youtubeUrl: string): string {
  let videoId = ''
  if (youtubeUrl.includes('youtube.com/watch')) {
    const url = new URL(youtubeUrl)
    videoId = url.searchParams.get('v') || ''
  } else if (youtubeUrl.includes('youtu.be')) {
    videoId = youtubeUrl.split('/').pop() || ''
  } else if (youtubeUrl.includes('youtube.com/embed/')) {
    videoId = youtubeUrl.split('/').pop() || ''
  }
  return youtubeUrl ? `https://www.youtube.com/embed/${videoId}?rel=0` : ''
}

/**
 * @returns {BlogArticleObj[]} Articles mockés (fallback API).
 */
function getMockBlogArticles(): BlogArticleObj[] {
  return [
    {
      id: 1,
      Title: "Comment choisir sa ville d'études ?",
      Description:
        'Découvre les critères essentiels pour choisir la ville parfaite pour tes études : qualité de vie, coût, opportunités professionnelles, et plus encore.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Vie étudiante',
      ReadingTime: 5,
      Link: '/nos-conseils/vie-etudiante/choisir-ville-etudes',
    },
    {
      id: 2,
      Title: "Étudier à l'étranger : les meilleures destinations",
      Description:
        "Explore les meilleures destinations pour étudier à l'étranger en fonction de ton profil, de ton budget et de tes aspirations professionnelles.",
      Image: '/images/emptyCoverPicture.png',
      Category: "Études à l'étranger",
      ReadingTime: 7,
      Link: '/nos-conseils/etudes-a-letranger/etudier-etranger',
    },
    {
      id: 3,
      Title: 'Top 10 des villes étudiantes en France',
      Description:
        'Découvre notre classement des meilleures villes étudiantes en France, basé sur la qualité des écoles, la vie étudiante, le coût de la vie et les opportunités de stage.',
      Image: '/images/emptyCoverPicture.png',
      Category: 'Vie étudiante',
      ReadingTime: 6,
      Link: '/nos-conseils/vie-etudiante/top-villes-etudiantes',
    },
  ]
}

/**
 * Charge les données SEO inter via `/api/aca/seointer` (SSR via `useAsyncData`).
 * Redirige automatiquement vers l'URL parente ou la home si le contenu est absent.
 * @param {UseSeoInterDataOptions} options Langue, type de page et paramètres route.
 * @returns {Promise<{ seoInterData: Ref<SeoInterDataResponse | null>; loading: ComputedRef<boolean>; error: ComputedRef<string | null>; lastHttpStatus: ComputedRef<number | undefined>; refresh: () => Promise<void> }>} Données et état de chargement.
 */
export const useSeoInterData = async (options: UseSeoInterDataOptions) => {
  const api = useFrontApi()
  const route = useRoute()
  const { locale, defaultLocale } = useI18n()
  const lastHttpStatus = ref<number | undefined>(undefined)

  const {
    data: seoInterData,
    error,
    status,
    refresh,
  } = await useAsyncData<SeoInterDataResponse | null>(
    () =>
      `seo-inter-${toValue(options.language)}-${options.pageType}-${resolveSeoInterParam(options.param1) ?? ''}-${resolveSeoInterParam(options.param2) ?? ''}`,
    async () => {
      const language = toValue(options.language)
      const pageType = options.pageType
      const param1 = resolveSeoInterParam(options.param1)
      const param2 = resolveSeoInterParam(options.param2)

      if (!language || !pageType) {
        lastHttpStatus.value = undefined
        return null
      }

      const endpoint: string =
        `/api/aca/seointer?language=${encodeURIComponent(language)}&pageType=${encodeURIComponent(pageType)}` +
        (param1 ? `&param1=${encodeURIComponent(param1)}` : '') +
        (param2 ? `&param2=${encodeURIComponent(param2)}` : '')

      try {
        const { data } = await api.get<SeoInterData>(endpoint)
        lastHttpStatus.value = 200
        data.youTubeUrl = getYouTubeEmbedUrl(data.youTubeUrl)

        return {
          seodatas: data,
          parsedParentBlock: parseBlock<SeoSiblingsBlock>(data.parentBlock, EMPTY_SEO_BLOCK),
          parsedSistersBlock: parseBlock<SeoSiblingsBlock>(data.sistersBlock, EMPTY_SEO_BLOCK),
          parsedChildrenBlock: parseBlock<SeoSiblingsBlock>(data.childrenBlock, EMPTY_SEO_BLOCK),
          parsedCousinsBlocks: parseBlock<SeoSiblingsBlock[]>(data.cousinsBlocks, []),
          partners: parseBlock<PartnerObj[]>(data.partners, []),
          programs: parseBlock<ProgramObj[]>(data.programs, []),
          articles: parseBlock<BlogArticleObj[]>(data.articles, getMockBlogArticles()),
        }
      } catch (e) {
        lastHttpStatus.value = getAxiosResponseStatus(e)
        return null
      }
    },
    {
      watch: [
        () => toValue(options.language),
        () => options.pageType,
        () => (options.param1 !== undefined ? (resolveSeoInterParam(options.param1) ?? '') : ''),
        () => (options.param2 !== undefined ? (resolveSeoInterParam(options.param2) ?? '') : ''),
      ],
    },
  )

  watch(
    [
      () => toValue(options.language),
      () => options.pageType,
      () => (options.param1 !== undefined ? (resolveSeoInterParam(options.param1) ?? '') : ''),
      () => (options.param2 !== undefined ? (resolveSeoInterParam(options.param2) ?? '') : ''),
    ],
    () => {
      lastHttpStatus.value = undefined
    },
  )

  const loading: ComputedRef<boolean> = computed<boolean>(() => status.value === 'pending')

  const errorMessage: ComputedRef<string | null> = computed<string | null>(() =>
    error.value ? (error.value.message ?? 'Erreur inconnue') : null,
  )

  const parentRedirectPath: ComputedRef<string | null> = computed(() => {
    const { sectionRoot, slugSegments } = parseSeoRouteContext(
      route.path,
      route.params.slug as string | string[] | undefined,
    )

    if (!shouldSeoInterManageParentRedirect(sectionRoot, slugSegments)) return null

    const isDead = shouldRedirectToSeoParent({
      loading: loading.value,
      httpStatus: lastHttpStatus.value,
      data: seoInterData.value,
      isEmptyPayload: (payload) =>
        isEmptySeoInterApiPayload((payload as SeoInterDataResponse).seodatas),
    })

    if (!isDead) return null

    const parent = resolveSeoParentRedirectPath({
      slugSegments,
      sectionRoot,
      locale: toValue(locale),
      defaultLocale: toValue(defaultLocale),
    })

    return route.path === parent ? null : parent
  })

  /**
   * À appeler depuis le setup de la page juste après `await useSeoInterData` :
   * `const nav = redirectIfDead(); if (nav) await nav`
   * (obligatoire pour que Nuxt applique la redirect en SSR).
   * @returns {ReturnType<typeof navigateTo> | undefined} Promesse de navigation si redirect, sinon `undefined`.
   */
  const redirectIfDead = (): ReturnType<typeof navigateTo> | undefined => {
    const parent = parentRedirectPath.value
    if (!parent) return undefined

    logInvalidSeoPath({
      fromPath: route.path,
      toPath: parent,
      locale: toValue(locale),
      source: 'useSeoInterData',
      httpStatus: lastHttpStatus.value,
    })

    return navigateTo(parent as RouteLocationRaw, { replace: true, redirectCode: 302 })
  }

  if (import.meta.client) {
    const router = useRouter()
    watch(parentRedirectPath, (parent) => {
      if (!parent || route.path === parent || loading.value) return
      void router.replace(parent)
    })
  }

  return {
    seoInterData,
    loading,
    error: errorMessage,
    lastHttpStatus: computed(() => lastHttpStatus.value),
    parentRedirectPath,
    redirectIfDead,
    refresh,
  }
}
